import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Popup {
  id: string;
  _id?: string;
  name: string;
  image: {
    url: string;
    publicId?: string;
  };
  linkUrl: string;
  linkTarget: '_self' | '_blank';
  showOnce: boolean;
  priority: number;
}

const POPUP_STORAGE_KEY = 'jarad_dismissed_popups';

const PopupModal = () => {
  const [popup, setPopup] = useState<Popup | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const fetchActivePopups = async () => {
      try {
        const apiUrl = import.meta.env.DEV
          ? '/api/v1/popups/active'
          : 'https://api.jarad.beeapp.es/api/v1/popups/active';

        const response = await fetch(apiUrl);
        const data = await response.json();

        if (data.success && data.data && data.data.length > 0) {
          // Get dismissed popups from localStorage
          const dismissedPopups = getDismissedPopups();

          // Find the first popup that hasn't been dismissed (if showOnce is true)
          const availablePopup = data.data.find((p: Popup) => {
            const popupId = p.id || p._id;
            if (p.showOnce && dismissedPopups.includes(popupId as string)) {
              return false;
            }
            return true;
          });

          if (availablePopup) {
            setPopup(availablePopup);
            // Delay showing the popup slightly for better UX
            setTimeout(() => setIsVisible(true), 500);
          }
        }
      } catch (error) {
        console.error('Error fetching popups:', error);
      }
    };

    fetchActivePopups();
  }, []);

  const getDismissedPopups = (): string[] => {
    try {
      const stored = localStorage.getItem(POPUP_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const dismissPopup = () => {
    if (popup && popup.showOnce) {
      const popupId = popup.id || popup._id;
      const dismissedPopups = getDismissedPopups();
      if (popupId && !dismissedPopups.includes(popupId)) {
        dismissedPopups.push(popupId);
        localStorage.setItem(POPUP_STORAGE_KEY, JSON.stringify(dismissedPopups));
      }
    }
    setIsVisible(false);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismissPopup();
  };

  const handleImageClick = () => {
    if (popup) {
      if (popup.linkTarget === '_blank') {
        window.open(popup.linkUrl, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = popup.linkUrl;
      }
      dismissPopup();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      dismissPopup();
    }
  };

  if (!popup) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3, type: 'spring', damping: 25 }}
            className="relative max-w-lg w-full"
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute -top-3 -right-3 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
              aria-label="Cerrar"
            >
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Popup image */}
            <div
              className="relative cursor-pointer overflow-hidden rounded-xl shadow-2xl"
              onClick={handleImageClick}
            >
              <img
                src={popup.image.url}
                alt={popup.name}
                className="w-full h-auto object-contain max-h-[80vh]"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PopupModal;
